import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Stethoscope,
    Package,
    Boxes,
    TrendingUp,
    ShoppingBag,
    Repeat,
    Users,
    ScrollText,
    LogOut,
    LifeBuoy,
    Bot,
    Menu,
    X,
    ExternalLink,
    Undo2,
    Tag,
    Sparkles,
    MessageCircle,
    TestTube,
    Shield,
    Building2,
    Receipt,
    Truck,
    ClipboardList,
    UploadCloud,
    Warehouse,
    History,
    Megaphone,
    Layers,
    BookOpen,
    AlertTriangle,
    Globe,
    Activity,
    Gift,
    Sparkle,
    Command as CommandIcon,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import ThemeToggle from "../components/ThemeToggle";
import Brand from "../components/Brand";
import CommandPalette from "./CommandPalette";

const NAV_GROUPS = [
    {
        label: "Overview",
        items: [
            { to: "/admin/dashboard", icon: LayoutDashboard, label: "Dashboard", role: ["admin", "pharmacist"] },
        ],
    },
    {
        label: "Catalog",
        items: [
            { to: "/admin/products", icon: Package, label: "Products", role: ["admin"] },
            { to: "/admin/products/bulk-upload", icon: UploadCloud, label: "Bulk upload", role: ["admin"] },
            { to: "/admin/inventory", icon: Boxes, label: "Inventory", role: ["admin"] },
            { to: "/admin/inventory/warehouse", icon: Warehouse, label: "Warehouse", role: ["admin", "pharmacist"] },
            { to: "/admin/inventory/batches", icon: Layers, label: "Batches", role: ["admin", "pharmacist"] },
            { to: "/admin/inventory/movements", icon: History, label: "Stock movements", role: ["admin", "pharmacist"] },
            { to: "/admin/inventory/recalls", icon: Megaphone, label: "Recalls", role: ["admin"] },
            { to: "/admin/inventory/forecast", icon: TrendingUp, label: "Inventory forecast", role: ["admin", "pharmacist"] },
            { to: "/admin/promos", icon: Tag, label: "Promo codes", role: ["admin"] },
        ],
    },
    {
        label: "Operations",
        items: [
            { to: "/admin/orders", icon: ShoppingBag, label: "Orders", role: ["admin", "pharmacist"] },
            { to: "/admin/prescriptions", icon: Stethoscope, label: "Prescriptions", role: ["admin", "pharmacist"] },
            { to: "/admin/returns", icon: Undo2, label: "Returns", role: ["admin", "pharmacist"] },
        ],
    },
    {
        label: "Healthcare",
        items: [
            { to: "/admin/consultations", icon: MessageCircle, label: "Consultations", role: ["admin", "pharmacist"] },
            { to: "/admin/lab-orders", icon: TestTube, label: "Lab orders", role: ["admin", "pharmacist"] },
            { to: "/admin/insurance-leads", icon: Shield, label: "Insurance leads", role: ["admin"] },
        ],
    },
    {
        label: "Support",
        items: [
            { to: "/admin/support", icon: LifeBuoy, label: "Tickets", role: ["admin", "pharmacist"] },
            { to: "/admin/complaints", icon: AlertTriangle, label: "Complaints", role: ["admin", "pharmacist"] },
            { to: "/admin/kb", icon: BookOpen, label: "Knowledge Base", role: ["admin"] },
            { to: "/admin/chat-conversations", icon: Bot, label: "AI chats", role: ["admin"] },
        ],
    },
    {
        label: "Customers",
        items: [
            { to: "/admin/users", icon: Users, label: "Users & roles", role: ["admin"] },
            { to: "/admin/subscriptions", icon: Repeat, label: "Subscriptions", role: ["admin"] },
            { to: "/admin/elite/subscriptions", icon: Sparkles, label: "Elite members", role: ["admin"] },
            { to: "/admin/refer-earn", icon: Gift, label: "Refer & Earn", role: ["admin"], badge: "v3" },
        ],
    },
    {
        label: "Analytics",
        items: [
            { to: "/admin/reminders-analytics", icon: Activity, label: "Reminders pulse", role: ["admin", "pharmacist"], badge: "v3" },
        ],
    },
    {
        label: "Wholesale (B2B)",
        items: [
            { to: "/admin/b2b/accounts", icon: Building2, label: "B2B accounts", role: ["admin"] },
            { to: "/admin/b2b/orders", icon: Receipt, label: "B2B orders", role: ["admin", "pharmacist"] },
        ],
    },
    {
        label: "Inventory ops",
        items: [
            { to: "/admin/vendors", icon: Truck, label: "Vendors", role: ["admin"] },
            { to: "/admin/purchase-orders", icon: ClipboardList, label: "Purchase orders", role: ["admin"] },
        ],
    },
    {
        label: "System",
        items: [
            { to: "/admin/audit", icon: ScrollText, label: "Audit log", role: ["admin"] },
            { to: "/admin/cms", icon: Globe, label: "UI Text CMS", role: ["admin"] },
        ],
    },
];

const AdminLayout = ({ children, title, subtitle, action }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate("/admin/login");
    };

    const Sidebar = (
        <aside
            className="flex h-screen w-64 flex-col border-r border-slate-200 bg-slate-50/80 backdrop-blur-sm dark:border-white/5 dark:bg-zinc-950/80"
            data-testid="admin-sidebar"
        >
            <div className="border-b border-slate-200 px-5 py-4 dark:border-white/5">
                <Brand size="sm" sublabel="Admin OS" testId="admin-brand" />
            </div>

            <nav className="flex-1 overflow-y-auto p-3" data-testid="admin-sidebar-nav">
                {NAV_GROUPS.map((group) => {
                    const items = group.items.filter((i) => i.role.includes(user?.role));
                    if (items.length === 0) return null;
                    return (
                        <div key={group.label} className="mb-5">
                            <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{group.label}</p>
                            <ul className="mt-1 space-y-0.5">
                                {items.map((i) => (
                                    <li key={i.to}>
                                        <NavLink
                                            to={i.to}
                                            data-testid={`admin-nav-${i.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                            end={i.to === "/admin/dashboard"}
                                            className={({ isActive }) =>
                                                `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-all ${
                                                    isActive
                                                        ? "bg-brand-600 text-white shadow-sm"
                                                        : "text-slate-700 hover:bg-slate-200/60"
                                                }`
                                            }
                                            onClick={() => setMobileOpen(false)}
                                        >
                                            <i.icon className="h-4 w-4" />
                                            <span className="flex-1">{i.label}</span>
                                            {i.badge && (
                                                <span className="ml-auto inline-flex items-center gap-0.5 rounded-full bg-gradient-to-r from-brand-600 to-violet-500 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-white shadow-sm">
                                                    <Sparkle className="h-2 w-2" />{i.badge}
                                                </span>
                                            )}
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                })}
            </nav>

            <div className="border-t border-slate-200 p-3">
                <div className="rounded-xl bg-white p-3 shadow-sm" data-testid="admin-sidebar-user">
                    <p className="line-clamp-1 text-sm font-medium text-slate-900">{user?.full_name || user?.email}</p>
                    <div className="mt-1 flex items-center gap-2">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                            user?.role === "admin" ? "bg-brand-600/10 text-brand-700" : "bg-amber-500/10 text-amber-700"
                        }`}>
                            {user?.role}
                        </span>
                        <a href="/" target="_blank" rel="noreferrer" className="text-[11px] text-slate-500 hover:text-brand-700">
                            <ExternalLink className="inline h-3 w-3" /> Store
                        </a>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
                        data-testid="admin-logout-btn"
                    >
                        <LogOut className="h-3.5 w-3.5" /> Sign out
                    </button>
                </div>
            </div>
        </aside>
    );

    return (
        <div className="min-h-screen bg-slate-100/40 text-slate-900 dark:bg-zinc-950 dark:text-zinc-100" data-testid="admin-layout">
            <div className="flex">
                <div className="hidden lg:block lg:fixed lg:inset-y-0 lg:left-0">{Sidebar}</div>

                {/* mobile drawer */}
                {mobileOpen && (
                    <div className="fixed inset-0 z-50 lg:hidden">
                        <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMobileOpen(false)} />
                        <div className="relative">{Sidebar}</div>
                    </div>
                )}

                <main className="flex-1 lg:ml-64">
                    <header
                        className="sticky top-0 z-30 flex items-center gap-3 border-b border-slate-200 bg-white/80 px-6 py-3 backdrop-blur-md dark:border-white/5 dark:bg-zinc-950/70"
                        data-testid="admin-topbar"
                    >
                        <button
                            className="rounded-md p-2 hover:bg-slate-100 dark:hover:bg-white/5 lg:hidden"
                            onClick={() => setMobileOpen(true)}
                            aria-label="Open menu"
                        >
                            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                        </button>
                        <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-zinc-500">Admin OS</p>
                            <h1 className="line-clamp-1 text-xl font-semibold tracking-tight text-slate-900 dark:text-zinc-100" data-testid="admin-page-title">
                                {title}
                            </h1>
                            {subtitle && <p className="line-clamp-1 text-xs text-slate-500 dark:text-zinc-400">{subtitle}</p>}
                        </div>
                        <ThemeToggle variant="admin" />
                        <button
                            onClick={() => {
                                // Dispatch synthetic Ctrl+K to open the palette (handled inside CommandPalette).
                                window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", ctrlKey: true }));
                            }}
                            className="hidden md:inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1 text-xs text-slate-500 hover:border-brand-200 hover:text-brand-700 dark:border-white/10 dark:text-zinc-400"
                            data-testid="admin-cmdk-hint"
                            title="Open command palette"
                        >
                            <CommandIcon className="h-3 w-3" /> K
                        </button>
                        {action}
                    </header>
                    <div className="p-6">{children}</div>
                </main>
            </div>
            {/* v2H — Global command palette (mounted once per admin layout) */}
            <CommandPalette />
        </div>
    );
};

export default AdminLayout;
